import {WIX_BOOKINGS, WIX_ECOM, WIX_NEW_STORES} from '@wix/app-definition-ids';
import {EditorSDK, PageRef, RouterData} from '@wix/editor-platform-sdk-types';
import {IHttpClient} from '@wix/http-client';
import {ContextParams, PanelResolveType} from '@wix/platform-editor-sdk';
import {StoresWidgetID} from '@wix/wixstores-client-core';
import {RouterPrefix} from '@wix/wixstores-client-core/dist/es/src/constants';
import {getAppVersion} from '@wix/wixstores-client-core/dist/src/app-version/appVersion';
import {APP_DEFINITION_ID, PageMap} from '@wix/wixstores-client-core/dist/src/constants';
import {delay} from '@wix/wixstores-client-core/dist/src/utils/delay';
import {ExperimentsApi} from '../../common/experiments/ExperimentsApi';
import {Translate} from '../../common/translations/translations';
import {ArticleId} from '../../editor-script/constants';
import {EcomPublicApi} from '../../editor-script/editorScript';
import {doTransaction} from '../../editor-script/transaction';
import {reportPremiumQuotaUsage} from '../app-reflow/premium';
import {AppReflowPremiumFeatureName, BLOCKS_PRODUCT_PAGE_APP_DEF_ID, FedopsInteractions} from '../constants';
import {getCategoryPageSiteStatus} from './categories/categoriesinstallationUtils';
import {markSiteWithCategoryPageStatus} from './categories/categoriesRolloutManagerUtils';
import {addBlocksProductPageToExistingSite, getCompRef} from './common';
import {checkIfPageExists, getNumOfPagesByTpaPageId, getProductPage, updateTpaPageIdIfNeeded} from './pages';

const PROGRESS_BAR_MIN_STEP_DELAY = 1000;

function getReplaceModalTranslationBaseKey(hasMultipleProductPages: boolean) {
  return hasMultipleProductPages ? 'migrateToOOI.replaceModal.multiVariants' : 'migrateToOOI.replaceModal.singlePage';
}

export async function reinstallCategoryPage(sdk: EditorSDK, options: ContextParams, ecomPublicApi: EcomPublicApi) {
  const categoryPageRef = await sdk.tpa.getPageRefByTPAPageId('', {tpaPageId: PageMap.CATEGORY});

  /* eslint-disable no-console */
  console.log('Removing Category Page...');
  await sdk.pages.remove('', {pageRef: categoryPageRef});

  return fixCategoryPage(sdk, options, ecomPublicApi);
}

export async function fixCategoryPage(sdk: EditorSDK, options: ContextParams, ecomPublicApi: EcomPublicApi) {
  /* eslint-disable no-console */
  const isCategorySite = await getCategoryPageSiteStatus(options);
  console.log(`Is site marked as a Category Page site? ${isCategorySite ? 'Yes' : 'No'}`);
  if (!isCategorySite) {
    console.log('Marking site as a Category page site...');
    await markSiteWithCategoryPageStatus(options.essentials.httpClient, ecomPublicApi.getInstance(), true);
    console.log('Done marking');
  }

  const isCategoryPageExists = await checkIfPageExists(sdk, PageMap.CATEGORY);
  console.log(`Is Category Page installed? ${isCategoryPageExists ? 'Yes' : 'No'}`);
  let categoryPageRef;
  if (!isCategoryPageExists) {
    console.log('Adding Category page...');
    categoryPageRef = await ecomPublicApi.addPage(PageMap.CATEGORY, WIX_NEW_STORES, true);
    await updateTpaPageIdIfNeeded(sdk, PageMap.CATEGORY);
    console.log('Done adding');
  } else {
    categoryPageRef = await sdk.tpa.getPageRefByTPAPageId('', {tpaPageId: PageMap.CATEGORY});
  }

  let routerRef = await ecomPublicApi.getRouterRefByPrefix(RouterPrefix.CATEGORY);
  console.log(`Has router? ${routerRef ? 'Yes' : 'No'}`);
  if (!routerRef) {
    console.log('Adding router...');
    routerRef = await ecomPublicApi.addRouter(RouterPrefix.CATEGORY);
    console.log('Done adding');
  }

  const router = await ecomPublicApi.getRouter(routerRef);
  console.log(`Is router connected? ${router.pages?.length ? 'Yes' : 'No'}`);
  if (!router || !router.pages?.length) {
    console.log('Connecting router...');
    await ecomPublicApi.connectPageToRouter({
      pageRef: categoryPageRef,
      routerRef,
      pageRoles: [RouterPrefix.CATEGORY],
    });
    console.log('Done connecting');
  }

  console.log('Done!');
}

export async function fixBlocksProductPage(sdk: EditorSDK, fedopsLogger: Record<string, any>) {
  /* eslint-disable no-console */
  const hasOldProductPage = await checkIfPageExists(sdk, PageMap.PRODUCT);
  console.log(`Is Product Page installed? ${hasOldProductPage ? 'Yes' : 'No'}`);

  const isBlocksProductPageAppInstalled = await sdk.application.isApplicationInstalled('', {
    appDefinitionId: BLOCKS_PRODUCT_PAGE_APP_DEF_ID,
  });
  console.log(`Is Blocks Product Page App installed? ${isBlocksProductPageAppInstalled ? 'Yes' : 'No'}`);
  if (isBlocksProductPageAppInstalled) {
    console.log('Uninstalling Blocks Product Page App...');
    const blocksProductPageAppPublicApi: Record<string, any> = await sdk.application.getPublicAPI('', {
      appDefinitionId: BLOCKS_PRODUCT_PAGE_APP_DEF_ID,
    });

    await blocksProductPageAppPublicApi?.uninstallApp();
  }

  setTimeout(() => {
    console.log('Running migration...');
    void addBlocksProductPageToExistingSite(sdk, fedopsLogger).then(() => console.log('Done!'));
  }, 20000);
}

async function removeRouterConnectedProductPages(sdk: EditorSDK, router: RouterData) {
  /* eslint-disable no-console */
  console.log(`[Migration] Removing ${router.pages.length} old product pages...`);
  for (const page of router.pages) {
    console.log(`[Migration] Removing page: ${page.pageRef.id} (roles: ${page.pageRoles.join(', ')})`);
    await sdk.pages.remove('', {pageRef: page.pageRef, shouldShowEditorRemovePanel: false});
  }
  console.log('[Migration] Finished removing old pages');
}

async function removeLegacyProductPageWithoutRouter(sdk: EditorSDK) {
  /* eslint-disable no-console */
  const legacyProductPage = await getProductPage(sdk);
  if (!legacyProductPage) {
    return;
  }

  const legacyProductPageRef = await sdk.tpa.getPageRefByTPAPageId('', {
    tpaPageId: PageMap.PRODUCT,
  });

  if (!legacyProductPageRef) {
    return;
  }

  console.log(`[Migration] Removing legacy product page without router: ${legacyProductPageRef.id}`);
  await sdk.pages.remove('', {
    pageRef: legacyProductPageRef,
    shouldShowEditorRemovePanel: false,
  });
}

async function removeExistingProductPages(sdk: EditorSDK, router?: RouterData) {
  if (router?.pages?.length) {
    await removeRouterConnectedProductPages(sdk, router);
    return;
  }

  await removeLegacyProductPageWithoutRouter(sdk);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export async function migrateToOoiProductPage(
  sdk: EditorSDK,
  fedopsLogger: Record<string, any>,
  ecomPublicApi: EcomPublicApi,
  addOoiProductPage: (shouldNavigate?: boolean) => Promise<PageRef>,
  httpClient: IHttpClient,
  metaSiteId: string,
  t: Translate
): Promise<boolean> {
  /* eslint-disable no-console */
  const getAssetsUrl = () =>
    `https://static.parastorage.com/services/wixstores-client-worker/${getAppVersion()}/assets`;

  try {
    fedopsLogger?.interactionStarted?.(FedopsInteractions.MigrateToOoiProductPage);
    console.log('[Migration] Starting migration to new Product Page...');

    const numOfRemovedReflowPages = await getNumOfPagesByTpaPageId(sdk, PageMap.PRODUCT_PAGE_VARIANT);
    const hasMultipleProductPages = numOfRemovedReflowPages > 0;
    const replaceModalTranslationBaseKey = getReplaceModalTranslationBaseKey(hasMultipleProductPages);

    // Confirmation modal
    fedopsLogger?.interactionStarted?.(FedopsInteractions.MigrateToOoiProductPageConfirmation);
    const confirmationModalAction = await sdk.editor.openErrorPanel('', {
      headerText: t(`${replaceModalTranslationBaseKey}.title`),
      shouldShowIllustration: false,
      topDescriptionText: t(`${replaceModalTranslationBaseKey}.body`),
      mainActionText: t(`${replaceModalTranslationBaseKey}.replace`),
      secondaryActionText: t(`${replaceModalTranslationBaseKey}.cancel`),
      helpId: ArticleId.migrateToOoiProductPage,
    });
    fedopsLogger?.interactionEnded?.(FedopsInteractions.MigrateToOoiProductPageConfirmation);

    if (confirmationModalAction !== PanelResolveType.MAIN_ACTION) {
      console.log('[Migration] Migration cancelled by user');
      fedopsLogger?.interactionEnded?.(FedopsInteractions.MigrateToOoiProductPage);
      return false;
    }

    try {
      fedopsLogger?.interactionStarted?.(FedopsInteractions.MigrateToOoiProductPageExecution);

      await sdk.editor.openProgressBar('', {
        title: t('migrateToOOI.progressBar.title'),
        totalSteps: 3,
        currentStep: 1,
        stepTitle: t('migrateToOOI.progressBar.description.stepOne'),
      });

      // `openProgressBar` may render the initial step incorrectly, so force the first
      // visible state through `updateProgressBar` while keeping later steps unchanged.
      await sdk.editor.updateProgressBar('', {
        currentStep: 1,
        stepTitle: t('migrateToOOI.progressBar.description.stepOne'),
      });

      // Step 1: Get router and check app installation status
      const routerRef = await ecomPublicApi.getRouterRefByPrefix(RouterPrefix.PRODUCT_PAGE);
      console.log(`[Migration] Has Product Page router? ${routerRef ? 'Yes' : 'No'}`);

      let router;
      if (routerRef) {
        router = await ecomPublicApi.getRouter(routerRef);
        console.log(`[Migration] Router prefix: ${router.prefix}`);
        console.log(`[Migration] Router connected pages: ${router.pages?.length ?? 0}`);
      }

      const isBlocksProductPageAppInstalled = await sdk.application.isApplicationInstalled('', {
        appDefinitionId: BLOCKS_PRODUCT_PAGE_APP_DEF_ID,
      });
      console.log(
        `[Migration] Is Blocks Product Page App installed? ${isBlocksProductPageAppInstalled ? 'Yes' : 'No'}`
      );

      console.log(`[Migration] Product page variants to remove: ${numOfRemovedReflowPages}`);

      // Step 2: Execute migration in a single transaction
      await doTransaction(sdk, async () => {
        // Uninstall Blocks Product Page App if installed
        if (isBlocksProductPageAppInstalled) {
          console.log('[Migration] Uninstalling Blocks Product Page App...');
          const blocksProductPageAppPublicApi: Record<string, any> = await sdk.application.getPublicAPI('', {
            appDefinitionId: BLOCKS_PRODUCT_PAGE_APP_DEF_ID,
          });
          await blocksProductPageAppPublicApi?.uninstallApp();
          console.log('[Migration] Finished uninstalling Blocks Product Page App');
        }

        await removeExistingProductPages(sdk, router);

        await sdk.editor.updateProgressBar('', {
          currentStep: 2,
          stepTitle: t('migrateToOOI.progressBar.description.stepTwo'),
        });

        // Add new Product Page and connect to router
        console.log('[Migration] Adding new Product Page...');
        const pageRef = await addOoiProductPage(true);
        console.log(`[Migration] Added new Product Page: ${pageRef.id}`);

        await sdk.editor.updateProgressBar('', {
          currentStep: 3,
          stepTitle: t('migrateToOOI.progressBar.description.stepThree'),
        });

        if (routerRef) {
          console.log('[Migration] Connecting new page to router...');
          await ecomPublicApi.connectPageToRouter({
            pageRef,
            routerRef,
            pageRoles: [RouterPrefix.PRODUCT_PAGE],
          });
          console.log('[Migration] Finished connecting to router');
        }
      });

      await delay(PROGRESS_BAR_MIN_STEP_DELAY);

      await sdk.editor.closeProgressBar('', {});
      console.log('[Migration] Migration completed successfully');
      fedopsLogger?.interactionEnded?.(FedopsInteractions.MigrateToOoiProductPageExecution);
    } catch (error) {
      fedopsLogger?.interactionFailed?.(FedopsInteractions.MigrateToOoiProductPageExecution, error as Error);
      throw error;
    }

    if (numOfRemovedReflowPages > 0) {
      await reportPremiumQuotaUsage(
        AppReflowPremiumFeatureName.ProductPage,
        httpClient,
        metaSiteId,
        `-${numOfRemovedReflowPages}`
      );
      console.log(`[Migration] Reported premium quota decrement: -${numOfRemovedReflowPages}`);
    }

    await delay(PROGRESS_BAR_MIN_STEP_DELAY);

    // Success modal
    const successModalAction = await sdk.editor.openPromotionalPanel('', {
      titleText: t('migrateToOOI.successModal.title'),
      illustration: `${getAssetsUrl()}/images/migrate-to-ooi-success.svg`,
      subtitleText: t('migrateToOOI.successModal.body'),
      contentArray: [],
      mainActionText: t('migrateToOOI.successModal.CTA'),
      KBLinkText: t('migrateToOOI.successModal.learnMore'),
      helpId: ArticleId.migrateToOoiProductPage,
    } as any);

    console.log(`[Migration] Success modal closed with action: ${successModalAction}`);
    fedopsLogger?.interactionEnded?.(FedopsInteractions.MigrateToOoiProductPage);
    return true;
  } catch (error) {
    console.error('[Migration] Error during migration:', error);
    fedopsLogger?.interactionFailed?.(FedopsInteractions.MigrateToOoiProductPage, error as Error);
    await Promise.resolve(sdk.editor.closeProgressBar('', {})).catch(() => {
      // Ignore errors when closing progress bar
    });
    throw error;
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export async function analyzeStoresInstallation(sdk: EditorSDK, options: ContextParams, ecomPublicApi: EcomPublicApi) {
  /* eslint-disable no-console */
  const titleStyle = 'color: navy; font-family:sans-serif; font-size: 18px; text-decoration: underline';
  const subTitleStyle = 'color: orchid; font-family:sans-serif; font-size: 14px; text-decoration: underline';

  console.log(`%cApps`, titleStyle);
  console.log('');

  const isEcomPlatformAppInstalled = await sdk.application.isApplicationInstalled('', {appDefinitionId: WIX_ECOM});
  const isStoresAppInstalled = await sdk.application.isApplicationInstalled('', {appDefinitionId: WIX_NEW_STORES});
  const isBookingsAppInstalled = await sdk.application.isApplicationInstalled('', {appDefinitionId: WIX_BOOKINGS});
  console.log(`Is eCom Platform app installed? ${isEcomPlatformAppInstalled ? 'Yes' : 'No'}`);
  console.log(`Is Online Stores app installed? ${isStoresAppInstalled ? 'Yes' : 'No'}`);
  console.log(`Is Bookings app installed? ${isBookingsAppInstalled ? 'Yes' : 'No'}`);
  console.log('');

  console.log(`%cPages`, titleStyle);
  console.log('');

  console.log(`%cCategory Page`, subTitleStyle);
  console.log('');
  const isCategorySite = await getCategoryPageSiteStatus(options);
  console.log(`Is site marked as a Category Page site? ${isCategorySite ? 'Yes' : 'No'}`);
  const isCategoryPageExists = await checkIfPageExists(sdk, PageMap.CATEGORY);
  console.log(`Is Category Page installed? ${isCategoryPageExists ? 'Yes' : 'No'}`);
  const routerRef = await ecomPublicApi.getRouterRefByPrefix(RouterPrefix.CATEGORY);
  console.log(`Has router? ${routerRef ? 'Yes' : 'No'}`);
  const router = await ecomPublicApi.getRouter(routerRef);
  console.log(`Is router connected? ${router.pages?.length ? 'Yes' : 'No'}`);
  console.log('');

  console.log(`%cProduct Page`, subTitleStyle);
  console.log('');
  const hasOldProductPage = await checkIfPageExists(sdk, PageMap.PRODUCT);
  console.log(`Is Product Page installed? ${hasOldProductPage ? 'Yes' : 'No'}`);
  const isBlocksProductPageAppInstalled = await sdk.application.isApplicationInstalled('', {
    appDefinitionId: BLOCKS_PRODUCT_PAGE_APP_DEF_ID,
  });
  console.log(`Is Blocks Product Page App installed? ${isBlocksProductPageAppInstalled ? 'Yes' : 'No'}`);
  console.log('');

  console.log(`%cCart Page`, subTitleStyle);
  console.log('');
  const cartPageExists = await checkIfPageExists(sdk, PageMap.CART);
  console.log(`Is Cart Page installed? ${cartPageExists ? 'Yes' : 'No'}`);
  console.log('');

  console.log(`%cCheckout Page`, subTitleStyle);
  console.log('');
  const checkoutPageExists = await checkIfPageExists(sdk, PageMap.CHECKOUT);
  console.log(`Is Checkout Page installed? ${checkoutPageExists ? 'Yes' : 'No'}`);
  console.log('');

  console.log(`%cThank You Page`, subTitleStyle);
  console.log('');
  const thankYouPageExists = await checkIfPageExists(sdk, PageMap.THANKYOU);
  console.log(`Is Thank You Page installed? ${thankYouPageExists ? 'Yes' : 'No'}`);
  console.log('');

  if (isEcomPlatformAppInstalled) {
    let ecomComps;
    if (!ExperimentsApi.isOnlineStoresRemoveDeprecatedAppIdExperimentEnabled) {
      const applicationId = (await sdk.tpa.app.getDataByAppDefId('', WIX_ECOM)).applicationId;
      ecomComps = await sdk.document.tpa.app.getAllCompsByApplicationId('', applicationId);
    } else {
      ecomComps = await sdk.document.tpa.app.getAllComps('', WIX_ECOM);
    }

    const countPerComp: {[id: string]: number} = {};
    for (const comp of ecomComps) {
      const entry = Object.entries(StoresWidgetID).find((e) => e[1] === comp.widgetId);
      const widgetName = entry?.[0].replace(/_/g, ' ') || comp.widgetId;
      countPerComp[widgetName] = countPerComp[widgetName] ? countPerComp[widgetName] + 1 : 1;
    }

    console.log(`%cAll Components`, titleStyle);
    console.log('');
    for (const compKey of Object.keys(countPerComp)) {
      console.log(`${compKey}: ${countPerComp[compKey]}`);
    }
    console.log('');
  }
}

export async function fixProductPageFontStyleParams(sdk: EditorSDK, ecomPublicApi: EcomPublicApi) {
  const allComponents = await sdk.document.tpa.app.getAllComps('', APP_DEFINITION_ID);
  const productPageComponent = allComponents.filter((comp) => comp.widgetId === StoresWidgetID.PRODUCT_PAGE);
  for (const comp of productPageComponent) {
    const productPageCompRef = await getCompRef(sdk, comp);

    const fontValues = {
      productPage_titleFontStyle: 32,
      productPage_priceFontStyle: 26,
      productPage_descriptionFontStyle: 15,
      productPage_quantityFontStyle: 16,
      productPage_infoSectionTitleFontStyle: 16,
      productPage_infoSectionTextFontStyle: 16,
    };

    const fontStyleParams = [
      'productPage_titleFontStyle',
      'productPage_priceFontStyle',
      'productPage_descriptionFontStyle',
      'productPage_quantityFontStyle',
      'productPage_infoSectionTitleFontStyle',
      'productPage_infoSectionTextFontStyle',
    ];

    const styleParams = fontStyleParams.map((fontStyleParamKey) => {
      const fixedFontStyleParam = {
        fontStyleParam: true as const,
        family: 'helvetica-w01-roman',
        preset: 'Custom',
        size: fontValues[fontStyleParamKey],
        style: {
          bold: false,
          italic: false,
          underline: false,
        },
      };
      return {
        type: 'font' as const,
        key: fontStyleParamKey,
        param: {
          value: fixedFontStyleParam,
        },
      };
    });

    await ecomPublicApi.setStyleParams(productPageCompRef, styleParams);
  }
}
